import React, {
  CSSProperties,
  forwardRef,
  MouseEvent,
  ReactNode,
  Ref,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import cl from 'classnames';
import { useLanguage } from 'src/hooks';
import { uuidV4 } from 'src/helper';
import { Language } from 'src/interface';
import {
  CalendarDate,
  DateRange,
  enMonths,
  enWeekDays,
  getSelectedClassName,
  ruMonths,
  ruWeekDays,
} from './calendar.utils';
import styles from './calendarStyle.module.scss';

type CalendarProps = {
  show: boolean;
  className?: string;
  style?: CSSProperties;
  selectedDate?: CalendarDate;
  onSelectDate?: (date: CalendarDate) => void;
  selectedDateRange?: DateRange;
  onSelectDateRange?: (dateRange: DateRange) => void;
  isRange?: boolean;
};

export const CalendarComponent = (
  {
    show,
    className,
    style,
    selectedDate,
    onSelectDate,
    selectedDateRange,
    onSelectDateRange,
    isRange = false,
  }: CalendarProps,
  ref: Ref<HTMLDivElement>,
) => {
  const { currentLang } = useLanguage();

  const uuid = useMemo(uuidV4, []);
  const [weekDays, months] = useMemo(() => {
    if (currentLang === Language.EN) {
      return [enWeekDays, enMonths];
    }

    return [ruWeekDays, ruMonths];
  }, [currentLang]);

  const currentDate = useMemo(() => new CalendarDate(), []);
  const initDate = useMemo(() => {
    if (isRange && selectedDateRange?.start?.date) {
      return selectedDateRange.start;
    }

    if (!isRange && selectedDate?.date) {
      return selectedDate;
    }

    return currentDate;
  }, [currentDate, isRange, selectedDate, selectedDateRange?.start]);

  const [selectedStart, selectedEnd] = useMemo(
    () =>
      selectedDateRange?.reverse
        ? [selectedDateRange?.end, selectedDateRange?.start]
        : [selectedDateRange?.start, selectedDateRange?.end],
    [selectedDateRange],
  );

  const [switchedDate, setSwitchedDate] = useState<{
    month: number;
    year: number;
  }>({
    month: initDate.month,
    year: initDate.year,
  });
  const [mode, setMode] = useState<'calendar' | 'month' | 'year'>('calendar');

  useEffect(() => {
    if (show) {
      return;
    }

    setSwitchedDate({
      month: initDate.month,
      year: initDate.year,
    });
    setMode('calendar');
  }, [show, initDate.month, initDate.year]);

  useEffect(() => {
    if (!show) {
      return;
    }

    if (isRange) {
      if (selectedStart?.isMovable) {
        const date = selectedStart.date ? selectedStart : currentDate;

        setSwitchedDate({
          month: date.month,
          year: date.year,
        });
        return;
      }
      if (selectedEnd?.isMovable && selectedEnd.date) {
        setSwitchedDate({
          month: selectedEnd.month,
          year: selectedEnd.year,
        });
      }
    } else {
      if (selectedDate?.isMovable) {
        if (selectedDate.date) {
          setSwitchedDate({
            month: selectedDate.month,
            year: selectedDate.year,
          });
        }
      }
    }
  }, [isRange, currentDate, selectedDate, selectedStart, selectedEnd, show]);

  const firstDay = useMemo(() => {
    const date = new CalendarDate(
      new Date(switchedDate.year, switchedDate.month - 1, 1, 0, 0, 0, 0),
    );
    date.setDay(-date.weekDay);

    return date;
  }, [switchedDate.month, switchedDate.year]);

  const lastDay = useMemo(() => {
    const date = new CalendarDate(firstDay.date);
    date.setDay(41);

    return date;
  }, [firstDay.date]);

  const daysList = useMemo(() => {
    const cycleDate = new CalendarDate(firstDay.date);

    const list: ReactNode[] = weekDays.map((weekDay, index) => (
      <div key={`cal-${uuid}-weekDay-${index}`} className={styles.weekDay}>
        {weekDay}
      </div>
    ));

    while (cycleDate.time < lastDay.time + 1) {
      const date = new CalendarDate(cycleDate.date);
      const selectedClassName = getSelectedClassName(
        isRange,
        date.time || undefined,
        isRange
          ? selectedStart?.time || undefined
          : selectedDate?.time || undefined,
        selectedEnd?.time || undefined,
      );

      list.push(
        <div
          key={`cal-${uuid}-day-${date.time}`}
          className={cl(
            styles.day,
            switchedDate.month === date.month && styles.currentMonth,
            currentDate.time === date.time && styles.currentDay,
            selectedClassName,
          )}
          onClick={(event) => {
            event.stopPropagation();

            if (!isRange) {
              onSelectDate?.(new CalendarDate(date.date));
              return;
            }

            if (Boolean(selectedStart?.date) === Boolean(selectedEnd?.date)) {
              onSelectDateRange?.({
                start: new CalendarDate(date.date),
                end: null,
              });
              return;
            }

            const start = selectedStart?.date
              ? selectedStart
              : selectedEnd || null;
            const end = new CalendarDate(date.date);

            if (Number(start?.time) > end.time) {
              onSelectDateRange?.({
                start: end,
                end: start,
              });
              return;
            }

            onSelectDateRange?.({ start, end });
          }}
        >
          {date.day}
        </div>,
      );
      cycleDate.setDay(1);
    }

    return list;
  }, [
    firstDay.date,
    weekDays,
    lastDay.time,
    uuid,
    isRange,
    selectedDate?.time,
    selectedStart,
    selectedEnd,
    switchedDate.month,
    currentDate.time,
    onSelectDate,
    onSelectDateRange,
  ]);

  const monthList = useMemo(() => {
    const date = isRange ? selectedStart : selectedDate;
    const start = date?.date ? (date.year - 2000) * 12 + date.month : undefined;
    const end = selectedEnd?.date
      ? (selectedEnd.year - 2000) * 12 + selectedEnd.month
      : undefined;

    return months.map((month, index) => {
      const selectedClassName = getSelectedClassName(
        isRange,
        (switchedDate.year - 2000) * 12 + index + 1,
        start,
        end,
      );

      return (
        <div
          key={`cal-${uuid}-month-${index}`}
          className={cl(styles.switchItem, selectedClassName)}
          onClick={(event) => {
            event.stopPropagation();
            setSwitchedDate((prevState) => ({
              ...prevState,
              month: index + 1,
            }));
            setMode('calendar');
          }}
        >
          {month}
        </div>
      );
    });
  }, [
    isRange,
    switchedDate.year,
    selectedDate,
    selectedStart,
    selectedEnd,
    months,
    uuid,
  ]);

  const yearsList = useMemo(() => {
    const list: ReactNode[] = [];
    const switchedYearStart = switchedDate.year - 4;

    for (
      let index = switchedYearStart;
      index < switchedYearStart + 12;
      index += 1
    ) {
      const selectedClassName = getSelectedClassName(
        isRange,
        index,
        isRange ? selectedStart?.year : selectedDate?.year || undefined,
        selectedEnd?.year,
      );

      list.push(
        <div
          className={cl(styles.switchItem, selectedClassName)}
          key={`cal-${uuid}-year-${index}`}
          onClick={(event) => {
            event.stopPropagation();
            setSwitchedDate((prevState) => ({ ...prevState, year: index }));
            setMode('month');
          }}
        >
          {index}
        </div>,
      );
    }

    return list;
  }, [
    isRange,
    switchedDate.year,
    selectedDate?.year,
    selectedStart?.year,
    selectedEnd?.year,
    uuid,
  ]);

  const leftClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();

      switch (mode) {
        case 'calendar': {
          setSwitchedDate((prevState) => ({
            month: prevState.month === 1 ? 12 : prevState.month - 1,
            year: prevState.month === 1 ? prevState.year - 1 : prevState.year,
          }));
          break;
        }
        case 'month': {
          setSwitchedDate((prevState) => ({
            ...prevState,
            year: prevState.year - 1,
          }));
          break;
        }
        case 'year': {
          setSwitchedDate((prevState) => ({
            ...prevState,
            year: prevState.year - 12,
          }));
        }
      }
    },
    [mode],
  );

  const rightClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      switch (mode) {
        case 'calendar': {
          setSwitchedDate((prevState) => ({
            month: prevState.month === 12 ? 1 : prevState.month + 1,
            year: prevState.month === 12 ? prevState.year + 1 : prevState.year,
          }));
          break;
        }
        case 'month': {
          setSwitchedDate((prevState) => ({
            ...prevState,
            year: prevState.year + 1,
          }));
          break;
        }
        case 'year': {
          setSwitchedDate((prevState) => ({
            ...prevState,
            year: prevState.year + 12,
          }));
        }
      }
    },
    [mode],
  );

  const displayedYear = useMemo(() => {
    switch (mode) {
      case 'calendar':
      case 'month':
        return switchedDate.year;
      case 'year':
        return `${switchedDate.year - 4} - ${switchedDate.year + 7}`;
    }
  }, [mode, switchedDate.year]);

  return (
    <div
      className={cl(styles.calendarContainer, show && styles.show, className)}
      style={style}
      ref={ref}
    >
      <div className={styles.controlSection}>
        <div className={styles.arrowSection}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10 12L6 8L10 4"
              stroke="#002855"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <div className={styles.arrowButton} onClick={leftClick} />
        </div>
        <div className={styles.switcher}>
          {mode === 'calendar' && (
            <div className={styles.month}>
              <div>{months[switchedDate.month - 1]}</div>
              <div
                className={styles.switcherButton}
                onClick={(event) => {
                  event.stopPropagation();
                  setMode('month');
                }}
              />
            </div>
          )}
          <div className={styles.year}>
            <div>{displayedYear}</div>
            <div
              className={cl(
                styles.switcherButton,
                mode === 'year' && styles.inactive,
              )}
              onClick={(event) => {
                event.stopPropagation();
                setMode('year');
              }}
            />
          </div>
        </div>
        <div className={styles.arrowSection}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6 4L10 8L6 12"
              stroke="#002855"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <div className={styles.arrowButton} onClick={rightClick} />
        </div>
      </div>
      {mode === 'calendar' && <div className={styles.calendar}>{daysList}</div>}
      {mode === 'month' && (
        <div className={styles.switcherContainer}>{monthList}</div>
      )}
      {mode === 'year' && (
        <div className={styles.switcherContainer}>{yearsList}</div>
      )}
    </div>
  );
};

export const Calendar = forwardRef(CalendarComponent);

export default Calendar;
